import React, { memo } from 'react';
import { styled } from '@compiled/react';
import Badge from '@atlaskit/badge';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { timeTrackingFormatterLongForm } from '@atlassian/jira-time-tracking-formatter/src/main';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import EstimateFieldWrapper from '../../estimate-field-wrapper';
import messages from '../messages';

export type Props = {
	value: string | undefined;
	hasEstimate: boolean;
	valueInSeconds?: number;
	timeTrackingOptions?: TimeTrackingOptions;
};

const DEFAULT_NO_VALUE = '0m';

export const OriginalEstimateFieldStatic = ({
	value,
	hasEstimate,
	valueInSeconds,
	timeTrackingOptions,
}: Props) => {
	const intl = useIntl();

	const longFormValue =
		hasEstimate && valueInSeconds && timeTrackingOptions
			? timeTrackingFormatterLongForm(valueInSeconds, timeTrackingOptions, intl)
			: undefined;
	const ariaLabel = longFormValue
		? `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${longFormValue}`
		: `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${intl.formatMessage(messages.originalEstimateAriaLabelNullValue)}`;

	return (
		<OriginalEstimateContainer>
			{hasEstimate && (
				<EstimateFieldWrapper
					tooltipLabel={intl.formatMessage(messages.originalEstimateTooltip)}
					ariaLabel={ariaLabel}
				>
					<OriginalEstimateWrapper>
						<Badge appearance="default">{value ?? DEFAULT_NO_VALUE}</Badge>
					</OriginalEstimateWrapper>
				</EstimateFieldWrapper>
			)}
		</OriginalEstimateContainer>
	);
};

export default memo<Props>(OriginalEstimateFieldStatic);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OriginalEstimateWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	boxSizing: 'border-box',
	border: `${
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		gridSize / 4
	}px solid transparent` /* ensure there's no visual diff between static lozenge and BENTO's component */,
	whiteSpace: 'nowrap',
	cursor: 'default',
	width: 'fit-content',
	overflow: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 10}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&>span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${gridSize * 10}px`,
		display: 'initial',
		lineHeight: 1,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OriginalEstimateContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 10}px`,
});
